import { useCallback, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { DndContext } from "@dnd-kit/core";
import { useSession } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { useCalendarContext } from "../../../providers";
import { WorkOrderAttemptDrawer } from "../WorkOrderAttemptDrawer/WorkOrderAttemptDrawer";
import { AssignmentView } from "./AssignmentView";
import {
  CalendarVerticalTabs,
  type TabGroupItem,
} from "./CalendarVerticalTabs";
import { SchedulerView } from "./SchedulerView";
import {
  DEFAULT_GROUP,
  SchedulerViewType,
  TabGroupKey,
  useNHCPrioritySchedulerTabs,
} from "./useNHCPrioritySchedulerTabs";

export function NHCPriorityScheduler() {
  const { classes } = useStyles();
  const { session } = useSession();
  const [activeGroup, setActiveGroup] = useState<TabGroupKey>(DEFAULT_GROUP);

  const {
    events,
    calendarSet,
    assignableUsers,
    schedulerUsers,
    selectedAttempt,
    setSelectedAttempt,
  } = useCalendarContext();
  const workTemplateBases = calendarSet?.config?.workTemplateBases ?? [];
  const tabsWithItems = useNHCPrioritySchedulerTabs(workTemplateBases, events);

  const isAssignee =
    !!session?.userId &&
    assignableUsers?.some((user) => user.id === session.userId);

  const isScheduler =
    !!session?.userId &&
    schedulerUsers?.some((user) => user.id === session.userId);

  const assigneeMode = isAssignee && !isScheduler;
  const assigneeId = assigneeMode ? session?.userId : null;
  const assigneeModeUser = assignableUsers?.find(
    (user) => user.id === assigneeId
  );

  const handleSelectionChange = useCallback((group: TabGroupKey) => {
    setActiveGroup(group);
  }, []);

  const handleSubItemPress = useCallback(
    (_group: TabGroupKey, _item: TabGroupItem) => {
      // TODO: ABK filter assignment / regular view data by spec sheet (sub-item) selection
    },
    []
  );

  const activeView = assigneeMode
    ? SchedulerViewType.Scheduler
    : tabsWithItems[activeGroup].view;

  return (
    <Box className={classes.container}>
      <DndContext>
        {!assigneeMode && (
          <CalendarVerticalTabs
            groups={tabsWithItems}
            defaultGroup={DEFAULT_GROUP}
            onSelectionChange={handleSelectionChange}
            onSubItemPress={handleSubItemPress}
          />
        )}
        <Box className={classes.mainView}>
          {activeView === SchedulerViewType.Assignment && <AssignmentView />}
          {activeView === SchedulerViewType.Scheduler && (
            <SchedulerView assigneeModeUser={assigneeModeUser} />
          )}
          {selectedAttempt && (
            <WorkOrderAttemptDrawer
              key={selectedAttempt.id}
              open={!!selectedAttempt}
              listAttempt={selectedAttempt}
              onClose={() => setSelectedAttempt(null)}
            />
          )}
        </Box>
      </DndContext>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    padding: theme.spacing(1),
    height: "100%",
    width: "100%",
    gap: theme.spacing(2),
  },
  verticalTabs: {},
  topRow: {
    backgroundColor: theme.palette.background.default,
  },
  assignmentView: {
    flex: 1,
    minWidth: 0,
    padding: theme.spacing(1),
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  mainView: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    minHeight: 0,
  },
  calendarContainer: {
    display: "flex",
    flexDirection: "row",
    overflow: "auto",
    flex: 1,
  },
  boardView: {
    flexShrink: 0,
    overflow: "auto",
    display: "flex",
    flexDirection: "row",
    gap: theme.spacing(2),
  },
}));
